import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { GroupBy, CachedExpandedDetails, SICallTypes, ServiceInsightsByIssueData, ServiceMetricTypes, ByIssueMetrics, SICallTypesDatabaseCode } from '../../types/service-insight-types';
import { FiltersService } from 'src/app/core/services/filters.service';
import { PageEvent } from '@angular/material/paginator';
import { MetricDetailsRequest } from 'src/app/global/metric-details';
import { animate, style, transition, trigger } from '@angular/animations';
import { MetricsService } from 'src/app/core/services/metrics.service';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { Router } from '@angular/router';
import { ServiceInsightsExportPhotoService } from '../../services/service-insights-export-photo-service';
import { ServiceInsightsService } from '../../services/service-insights-service.service';
import { Subscription } from 'rxjs';
import { ServiceCustomerFilterOptions, ServiceCustomerFilters } from 'src/app/global/service-customer-model';


@Component({
    selector: 'service-details-table',
    templateUrl: './service-details-table.component.html',
    styleUrls: ['./service-details-table.component.scss'],
    animations: [
        trigger('inOutAnimation', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('.4s ease-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate('.4s ease-in', style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})

// TODO US620782: Component type safety is very poor. The component uses `any` for most of its data structures, 
// which makes it hard to reason about the code and increases the risk of runtime errors. 
// Refactor to use strongly typed interfaces and classes for the data structures used in this component.
export class ServiceDetailsTableComponent implements OnInit {
  @Input() title: string = "Title"
  @Input() callType: SICallTypes = SICallTypes.UntrackedCalls; // Will dictate the filtering rules and display logic for this component
  @Input() groupByValue: string = '';
  @Input() issueFilter: string = "";
  @Input() selectedAdvancedFilters:{ [id: string]: string[] } = {};

  // TODO: Why is this an output? The state is local to this component and not used anywhere else.
  @Output() showExpandedTableLoader = false;
  @Output() isExpandedTableLoader: EventEmitter<boolean> = new EventEmitter<boolean>();

  // Pagination
  totalRows = 0;
  pageSize = 100;
  currentPage = 0;
  pageSizeOptions: number[] = [25, 50, 75, 100];

  // Display
  /** @deprecated use @see ServiceCallTypeLabelPipe instead for obtaining the display label of service call types */
  unitLabel: string = "Regular Service Call";
  
  rowUnits: string = "Observations";
  noDataAvailable = false;
  isOnInitCalled: boolean = false;
  isToolbarLoading: boolean = false;

  // Data
  dataSource: any = [];
  data: any = [];
  result: any = [];
  groupedResult: any;
  request: any;
  filters: ContractModel | undefined;

  // Grouping / Row State
  hideRuleContent: boolean[] = [];
  expandedRows: Set<number> = new Set();
  sortedGroupedKeys: string[] = [];
  areAllRowsCollapsed: boolean = false;

  // Issue Metrics
  locationsData: any = [];
  issuesData: any = [];
  byIssuesData: ByIssueMetrics[] = [];

  // Advanced Filters
  advancedFilterData: ServiceCustomerFilters[] = [];
  excludeKeys: string[] = [];
  toggleAdvancedFilterSelection: boolean = false;

  private readonly callTypesWithRecurring: SICallTypes[] = [
    SICallTypes.ExtraServiceVisit,
    SICallTypes.RegularServiceCalls,
    SICallTypes.ProgramReviews,
  ];

  // Export Photo View
  isExportPhotoView: boolean = false;

  serviceCustomerFiltersSubscription: Subscription | undefined;

  // Comments Modal
  showCommentsModal: boolean = false;
  commentsModalText: string = '';

  // Participants Modal
  showParticipantsModal: boolean = false;
  participantNameList: string[] = [];

  SICallTypes = SICallTypes;

  SALESFORCE_BASE_URL = 'https://ecolab.lightning.force.com/lightning/r/WorkOrder/';
  
  constructor(
    private _filterService: FiltersService,
    private _serviceInsightsService: ServiceInsightsService,
    private _metricsService: MetricsService,
    private _mixpanelService: MixpanelService,
    private router: Router,
    private _exportPhotoService : ServiceInsightsExportPhotoService,
  ) {
  }

  async ngOnInit() {
    this._filterService.selectedFiltersObservable.subscribe(f => this.filters = f);

    if (!this.callTypesWithRecurring.includes(this.callType)) {
      this.excludeKeys = ['recurringIssue'];
    }

    if (this.filters !== undefined) {

      this.request = new MetricDetailsRequest(
        undefined, // Need not pass the filters
        "calls",
        this.callType,
        undefined,
        this.pageSize,
        this.currentPage + 1,
        this.getSortByValue(this.groupByValue),
        "asc",
        undefined // graphDetailFilter
      );

      this._subscribeToByIssuesData();

      // TODO: Carried over from previous implementation - it seems we have 
      // two calls for getMetricsDetails and getMetricsDetails. 
      // Should the advanced filters call be completed first individually 
      // and then the getMetricsDetails call be made?
      this.isToolbarLoading = true;
      const [, advancedFilters] = await Promise.all([
        this.getMetricsDetails(this.request),
        this._serviceInsightsService.getServiceInsightsAdvFilters(this.callType),
      ]);
      this.advancedFilterData = advancedFilters;
      this.isToolbarLoading = false;

      this._exportPhotoService.isExportPhotoViewObervable.subscribe(value => this.isExportPhotoView = value);

      this.serviceCustomerFiltersSubscription = this._serviceInsightsService.getServiceCustomerFilters().subscribe(async advFilter => {
        this.selectedAdvancedFilters = advFilter;

        this.request = new MetricDetailsRequest(
          this.request?.filters,
          this.request?.serviceType,
          this.request?.metricDetailType,
          this.request?.observationName,
          this.request?.limit,
          this.request?.page,
          this.request?.sortBy,
          this.request?.sortOrder,
          this.request?.graphDetailFilter,
          this.selectedAdvancedFilters as ServiceCustomerFilterOptions
        );

        await Promise.all([
          this.getMetricsDetails(this.request),
          this._fetchByIssuesData(true),
        ]);
      });
    }
    else{
      this.router.navigateByUrl('/filter');
      alert(`Please apply filters before navigating to the service metric details page`);
    }
    this.isOnInitCalled = true;
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['groupByValue'] && !changes['groupByValue'].firstChange) {
      this.issueFilter = '';
    }
    if(this.isOnInitCalled) {
      this.expandedRows.clear();

      var cachedObj : CachedExpandedDetails | undefined = JSON.parse(sessionStorage.getItem(this.getSessionStorageKey(this.groupByValue))!);

      this.request.sortBy = this.getSortByValue(this.groupByValue);
      this.request.sortOrder = (this.groupByValue === "endDate") ? "asc" : "desc";
      // if filter is applied then api call to get filtered data
      if (!cachedObj || this.issueFilter) {
        this.currentPage = 0; //reset page number if there is no cached data for this groupby
        this.request.page = this.currentPage + 1;
        await this.getMetricsDetails(this.request);
      }
      else {
        this.result = JSON.parse(cachedObj.expandedDetailsJson);
        this.currentPage = cachedObj.pageNumber;
        this.data = this.result.data.records;
        this.noDataAvailable = this.result.data.records.length == 0;
        if(this.data && this.data.length) {
          this.groupedResult = this.groupByProperty(this.data, this.groupByValue);
        }
      }
    }
  }


  toggle(index: number) {
    // toggle based on index
    this.hideRuleContent[index] = !this.hideRuleContent[index];

    this.areAllRowsCollapsed =
      this.groupedResult.length > 0 &&
      this.groupedResult.every((_: any, i: number) => this.hideRuleContent[i]);
  }

  toggleAll(b: boolean) {
    let i = 0;
    for (let group of this.groupedResult) {
      this.hideRuleContent[i++] = b;
    }

    this.areAllRowsCollapsed = b;
  }

  resetToggle() {
    this.hideRuleContent = [];
  }

  groupHeaderClick(row: any) {
    if (row.expanded) {
      row.expanded = false;
    } else {
      row.expanded = true;
    }
  }

  // TODO: Migrate to enums. Using enums will make it easier to get rid of these 
  // small helpers and use a single source of truth for the group by values.
  private getSessionStorageKey(key: string) : string {
    switch (key) {
      case "issues":
        return "expandedIssuesData";
      case "accountNumber":
        return "expandedLocationsData";
      case "endDate":
        return "expandedPeriodData";
    }
    return "";
  }

  private getSortByValue(key: string) : string {
    switch (key) {
      case "issues":
        return "issue";
      case "accountNumber":
        return "location";
      case "endDate":
        return "endDate";
    }
    return "";
  }

  private getTabName(key: string) : string {
    switch (key) {
      case "issues":
        return "By Issue";
      case "accountNumber":
        return "By Location";
      case "endDate":
        return "By Date";
    }
    return "";
  }

  async getMetricsDetails(request: MetricDetailsRequest) {
    this.resetToggle();
    this.showExpandedTableLoader = true;
    this.rowUnits = this.groupByValue === 'issues' ? 'Issues' : 'Observations';

    // Store the current tab
    const currentTab = this.groupByValue;

    // if filter is not applied then update the request accordingly
    if(!this.issueFilter) this.request.graphDetailFilter = undefined

    // if filter is  applied then update the request and retrive the information from the session storage
    if(this.issueFilter){
      this.request.graphDetailFilter = this.issueFilter;
    }

    let storageItem = this.getSessionStorageKey(this.groupByValue);

    try {
      const result = await this._serviceInsightsService.getSIExpandedMetricDetails(request);

      // Check if the current tab is still the same as when the request was made
      if (currentTab === this.groupByValue) {
        this.result = result;

        let itemToCache : CachedExpandedDetails = {
          pageNumber: this.currentPage,
          expandedDetailsJson: JSON.stringify(this.result),
        };

        try {
          if (!request?.graphDetailFilter) {
            sessionStorage.setItem(storageItem, JSON.stringify(itemToCache));
          }
        }
        catch (error) {
          if (!(error instanceof DOMException && error.name == "QuotaExceededError")) {
            throw error;
          }
        }

        this.noDataAvailable = this.result.data.records.length === 0;
        if(this.noDataAvailable) {
          this.totalRows = 0; // reset total rows to trigger the paginator to recalculate the page numbers based on new total count coming from API
        }

        this.data = this.result.data.records;
        if(this.data && this.data.length) {
          this.data.forEach((element: any, index: any) => {
            element.id = index
            element.endDate = element.endDate.split(" ")[0]
            element.siCallType = this.mapSICallTypeFromCode(element.callReason)
            element.virtualCallComments = this.mapCommentsFromSICode(element.callReason, element);
          });

          this.unitLabel = this._metricsService.mapServiceCallKeyToUnit(this.callType);

          this.totalRows = this.result.data.totalCount;

          this.groupedResult = this.groupByProperty(this.data, this.groupByValue);
        }
      }
    } finally {
      // Hide the loader after data loaded and all FE manipulations are done
      this.showExpandedTableLoader = false;
    }
  }

  private _subscribeToByIssuesData(): void {
    this._serviceInsightsService.getServiceInsightsByIssuesData(this.callType as keyof ServiceInsightsByIssueData).subscribe(d => {
      this.byIssuesData = d;
    });
  }

  private async _fetchByIssuesData(refresh = false): Promise<void> {
    if ((this.byIssuesData != undefined && this.byIssuesData.length == 0) || refresh) {
      this._serviceInsightsService.setServiceInsightsByIssuesData(
        this.callType as keyof ServiceInsightsByIssueData,
        await this._serviceInsightsService.getSIMetricByIssuesFromAPI(ServiceMetricTypes.Calls, this.callType, this.selectedAdvancedFilters as ServiceCustomerFilterOptions)
      );
    }
  }

  handleIssueFilterSelected(issueFilterName: string): void {
    this.issueFilter = issueFilterName;
    this.currentPage = 0;
    this.request.page = this.currentPage + 1;
    this.getMetricsDetails(this.request);
  }

  async onSelectedFiltersChange(selectedFilters: { [id: string]: string[] }): Promise<void> {
    this._serviceInsightsService.clearAllExpandedPhotos();
    const serviceCustomerFilters = selectedFilters as ServiceCustomerFilterOptions;
    this._serviceInsightsService.setServiceCustomerFilters(serviceCustomerFilters);
  }

  groupByProperty(data: any[], property: string): any[] {
    const groupedData: any = {};
    data?.forEach(item => {
      if (property === 'issues') {
        if (!groupedData[item.observationName]) {
          groupedData[item.observationName] = [];
        }
        groupedData[item.observationName].push(item);
      } else if (property === 'endDate') {
        let date = new Date(item[property]);
        let dateInGMT = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
        const value = dateInGMT.toLocaleDateString('en-US', {month: "long", day: "numeric", year: "numeric"});
        if (!groupedData[value]) {
          groupedData[value] = [];
        }
        groupedData[value].push(item);
      } else {
        const value = item["accountName"] +" - " + item[property] ;
        if (!groupedData[value]) {
          groupedData[value] = [];
        }
        groupedData[value].push(item);
      }
    });

    // Convert the grouped data into an array for easy iteration in HTML
    const resultArray = Object.entries(groupedData).map(([key, items]) => ({ "key": key, "items": items }));

    //sort result array according to group by property
    if (property != 'endDate' && property != 'issues') {
      resultArray.sort((a, b) => (b.items as unknown[]).length - (a.items as unknown[]).length);
    }

    return resultArray;
  }

  isGroup(index: number, item: GroupBy): boolean {
    return item.isGroupBy;
  }

  async onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;

    this.request = new MetricDetailsRequest(
      this.request?.filters,
      this.request?.serviceType,
      this.request?.metricDetailType,
      this.request?.observationName,
      event.pageSize,
      this.currentPage + 1,
      this.request?.sortBy,
      this.request?.sortOrder,
      this.request?.graphDetailFilter,
      this.request?.selectedAdvancedFilters
    );

    await this.getMetricsDetails(this.request);
  }

  isRowExpanded(index: number): boolean {
    return this.expandedRows.has(index);
  }

  toggleArrow(index: number) : void{
    if(this.expandedRows.has(index)) {
      this.expandedRows.delete(index);
    } else {
      this.expandedRows.add(index);
    }
  }

  openPhotoInNewTab(item: any) {
    this._mixpanelService.track('Export Photo Gallery Clicked');
    this._exportPhotoService.setServiceInsightsPhotoObject({
      photoInfo: item?.photoInfo?.map((info : any,index: number) => {
        return {
          id: index,
          url : info?.url ?? "",
          description: info?.description ?? "",
        }
      }) ?? [],
      shippingCity:item?.shippingCity ?? "",
      shippingState:item?.shippingState ?? "",
      accountName:item?.accountName ?? "",
      endDate:item?.endDate ?? "",
      ecolabRep:item?.ecolabRep ?? "",
      photoCardFooter: `${this.formatDate(item?.endDate)} | ${item?.ecolabRep}/${item.accountName}`,
      workOrderId: item?.workOrderId ?? ""
    });
    this._exportPhotoService.setIsExportPhotoView(true)
  }


  async downloadWorkOrder(pdfBlobUrl: string) {
    // Validate URL before processing to prevent Open Redirect attacks
    if (!this.isValidBlobStorageUrl(pdfBlobUrl)) {
      console.error('Invalid blob storage URL provided for download');
      return;
    }
    this.executeBrowserDownload(pdfBlobUrl);
    this.trackMixpanelEvent('View Report');
  }

  private executeBrowserDownload(url: string): void {
    const xhr = new XMLHttpRequest();
    xhr.responseType = 'blob';
    xhr.onload = () => {
      // Handle the blob response securely in isolation
      this.handlePdfBlobResponse(xhr.response);
    };

    // url to azure blob storage gets put together in backend and passed in response, same way as it is done for photos
    xhr.open('GET', url, true);
    xhr.send();
  }

  private handlePdfBlobResponse(blobData: unknown): void {
    // This method only handles blob responses - never receives the input URL parameter
    // Create blob from response
    const blob = new Blob([blobData as BlobPart], { type: "application/pdf" });
    
    // Generate local blob URL
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Only open locally-generated blob URLs (blob: protocol is always safe)
    if (blobUrl && blobUrl.startsWith('blob:')) {
      window.open(blobUrl);
    }
  }

  private isValidBlobStorageUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      // Accept only HTTPS URLs from blob storage
      return urlObj.protocol === 'https:' && 
             (urlObj.hostname.includes('blob.core.windows.net') || 
              urlObj.hostname.includes('azureedge.net'));
    } catch {
      return false;
    }
  }

  trackMixpanelEvent(linkName: string) {
    this._mixpanelService.track(linkName + ' Link Clicked', {
      "Call Type": this.title,
      "Tab Name": this.getTabName(this.groupByValue),
      "Page Name": "Service Insights Expanded Details"
    })
  }


  private formatDate(date: string) : string {
    let dateObj = new Date(date);
    let dateInGMT = new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * 60000);
    return dateInGMT.toLocaleDateString('en-US', {month: "long", day: "numeric", year: "numeric"});
  }

  /**
   * @deprecated Use hasComments() to resolve if a training session has a comment for its call reason.
   */
  getNoComments(item:any){
    let data = '';
    if ((!item.trainingCallComments && !item.esrCallComments && item.callReason === 'ESR') ||
    (!item.trainingCallComments && !item.installationCallComments && item.callReason === 'INST')) {
      data = 'No Comments';
    }
      return data;
  }

  /** Determines if a training session has any comments */
  hasComments(item: any): boolean {
    switch (item.callReason) {
      case 'ESR':
        return !!(item.trainingCallComments || item.esrCallComments);

      case 'INST':
        return !!(item.trainingCallComments || item.installationCallComments);

      case 'RSC':
      case 'PRC':
        return !!(item.trainingCallComments || item.observationComments);

      default:
        return !!item.trainingCallComments;
    }
  }

  isEMS(item: any): boolean {
    return item?.sourceSystemCode === 'EMS';
  }

  openCommentsModal(comments: string): void {
    this.commentsModalText = comments;
    this.showCommentsModal = true;
  }

  closeCommentsModal(): void {
    this.showCommentsModal = false;
    this.commentsModalText = '';
  }

  openParticipantsModal(participantNames: string | null | undefined): void {
    this.participantNameList = participantNames
      ? participantNames.split(',').map((name) => name.trim())
      : [];
    this.showParticipantsModal = true;
  }

  closeParticipantsModal(): void {
    this.showParticipantsModal = false;
    this.participantNameList = [];
  }

  getWorkOrderUrl(id: string): string {
    return `${this.SALESFORCE_BASE_URL}${id}/view`;
  }

  /**
   * Given the database code of a service call, return the SICallType.
   * @param callTypeCode
   * @returns
   */
  public mapSICallTypeFromCode(callTypeCode: string): SICallTypes {
    //Get SICallType given the database code
    let entry = Object.entries(SICallTypesDatabaseCode).find(
      ([key, value]) => value === callTypeCode,
    );
    let callType: SICallTypes =
      entry == undefined
        ? SICallTypes.UntrackedCalls
        : (entry[0] as SICallTypes);
    return callType;
  }

  /**
   * 
   * @param callTypeCode 
   */
  public mapCommentsFromSICode(callTypeCode: string,element:any): string{
    const siCallType = this.mapSICallTypeFromCode(callTypeCode);
    switch(siCallType){
      case SICallTypes.ExtraServiceVisit:
        return element.esrCallComments ?? '';
      case SICallTypes.Installations:
        return element.installationCallComments ?? '';
      case SICallTypes.TrainingSessions:
        return element.trainingCallComments ?? '';
      case SICallTypes.QuickCalls:
        return element.quickCallComments ?? '';
      case SICallTypes.RegularServiceCalls:
      case SICallTypes.ProgramReviews:
        return element.callSummaryNotes ?? '';
      default:
        return '';
    }
  }

  ngOnDestroy() {
    this.serviceCustomerFiltersSubscription?.unsubscribe();
  }
}
